import dayjs from 'dayjs';
import { cloneDeep, filter, map, reduce, size, sumBy } from 'lodash';
import { BusinessType } from '../redux/auth/authTypes';
import { InovicesType, TicketType, TicketItemType } from '../redux/data/dataTypes';

/**
 * Calculate the price for a single unit of the item based on unit information
 * @param item The ticket item
 * @returns The calculated price for a single unit of the item
 */
const calculateItemPrice = (item: TicketItemType): number => {
  // If unitPrice is provided, use it directly (price per custom unit)
  if (item.unitPrice !== undefined) {
    return item.unitPrice;
  }

  // If no unitPrice but we have a conversion factor, calculate the price per custom unit
  if (item.unitConversionFactor !== undefined) {
    return item.price * item.unitConversionFactor;
  }

  // Default to original price (price per piece)
  return item.price;
};

const getTicketTotal = (ticket: TicketType | InovicesType | undefined) => {
  if (!ticket) return '0.00';

  let sum = 0;

  // Calculate items total
  const items = ticket.items || [];
  if (items.length > 0) {
    items.forEach((item) => {
      const unitPrice = calculateItemPrice(item);
      sum += Number((unitPrice * item.quantity).toFixed(2));
    });
  }

  // Add modifier options
  const modifierOptions = ticket.modifierOptions || [];
  if (modifierOptions.length > 0) {
    modifierOptions.forEach((option) => {
      sum += Number((option.price * option.quantity).toFixed(2));
    });
  }

  // Apply discounts if any
  const discounts = ticket.discounts || [];
  if (discounts.length > 0) {
    // First apply fixed (VALUE) discounts
    const fixedDiscounts = discounts.filter((d) => d.type === 'VALUE');
    fixedDiscounts.forEach((discount) => {
      sum = Number((sum - discount.value).toFixed(2));
    });

    // Then apply percentage (PERCENT) discounts on the remaining amount
    const percentDiscounts = discounts.filter((d) => d.type === 'PERCENT');
    percentDiscounts.forEach((discount) => {
      const discountAmount = Number(((discount.value * sum) / 100).toFixed(2));
      sum = Number((sum - discountAmount).toFixed(2));
    });
  }

  // Ensure we don't return negative values
  return Math.max(0, sum).toFixed(2);
};

const getTicketSubTotal = (ticket?: InovicesType | TicketType): number => {
  if (!ticket) return 0;

  // Calculate items total using calculateItemPrice to handle custom units
  const itemsTotal = sumBy(ticket.items, (item) => {
    const unitPrice = calculateItemPrice(item);
    return Number((unitPrice * item.quantity).toFixed(2));
  });

  const modifiersTotal = sumBy(ticket.modifierOptions, (mod) =>
    Number((mod.price * mod.quantity).toFixed(2))
  );

  return Number((itemsTotal + modifiersTotal).toFixed(2));
};

const getTicketDiscount = (ticket?: InovicesType | TicketType): number => {
  if (!ticket || !ticket.discounts || ticket.discounts.length === 0) return 0;

  const subTotal = getTicketSubTotal(ticket);

  return Number(
    reduce(
      ticket.discounts,
      (sum, discount) => {
        if (discount.type === 'PERCENT') {
          return Number((sum + (discount.value / 100) * subTotal).toFixed(2));
        }
        return Number((sum + discount.value).toFixed(2));
      },
      0
    ).toFixed(2)
  );
};

const validateTicket = (ticket: TicketType) => {
  const clonnedTicket = cloneDeep(ticket);
  if (clonnedTicket.items) {
    clonnedTicket.items = filter(clonnedTicket.items, (d) => d.quantity > 0);
  }
  if (clonnedTicket.modifierOptions) {
    clonnedTicket.modifierOptions = filter(clonnedTicket.modifierOptions, (d) => d.quantity > 0);
  }
  return clonnedTicket;
};

const exportInvoiceHtml = (
  invoice: InovicesType,
  currency: string,
  business: BusinessType,
  qrCodeBase64?: string | null
) => {
  return ` <div style='width: 100% !important; box-sizing: border-box !important;'>
       ${business.logo ? `<img src='${business.logo}' style='max-height: 60px; display: block; margin: 0 auto;' />` : ``} 

      <!-- Main receipt table -->
      <table
        role='presentation'
        style='width: 100% !important; max-width: 380px !important; margin: 20px auto !important; background-color: white !important; border-radius: 8px !important; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1) !important; overflow: hidden !important; border-collapse: collapse !important;'
      >
        <!-- Top section with invoice type and centralized invoice number -->
        <tr style="direction: ltr;">
          <td style='text-align: center; padding: 16px; border-bottom: 1px solid #e2e8f0;'>
          ${business.name ? ` <p style='color: ${business.primaryColor}; font-weight: 600; font-size: 14px;'>${business.name}</p>` : ``}
           ${business.address ? ` <p style='color: #64748b; font-size: 13px; text-align: center;'>${business.address}</p>` : ``}
            ${
              business.phone
                ? ` <p style='color: #64748b; font-size: 13px; display: flex; justify-content: space-between; align-items: center;'>
                <span>Telephone:</span>
                <span>${business.phone}</span>
              </p>`
                : ``
            }
             
            <p style='color: #64748b; font-size: 13px; display: flex; justify-content: space-between; align-items: center;'>
              <span>Date | التاريخ:</span>
              <span>${dayjs(invoice?.createdAt).format('yyyy-mm-DD hh:mm a')}</span>
            </p>
            ${
              invoice?.store
                ? `<p style='color: #64748b; font-size: 13px; display: flex; justify-content: space-between; align-items: center;'>
                <span>Store Name | اسم الفرع:</span>
                <span>${invoice?.store?.name}</span>
              </p>`
                : ``
            }
               ${
                 invoice?.pos
                   ? ` <p style='color: #64748b; font-size: 13px; display: flex; justify-content: space-between; align-items: center;'>
                <span>POS Name | نقطة البيع:</span>
                <span>${invoice?.pos.name}</span>
              </p>`
                   : ``
               }
               
               ${
                 invoice?.creator
                   ? `  <p style='color: #64748b; font-size: 13px; display: flex; justify-content: space-between; align-items: center;'>
                <span>Sales Person | اسم البائع:</span>
                <span>${invoice?.creator?.name}</span>
              </p>`
                   : ``
               }
                 ${
                   invoice?.customer
                     ? `  <p style='color: #64748b; font-size: 13px; display: flex; justify-content: space-between; align-items: center;'>
                <span>Customer | العميل:</span>
                <span>${invoice?.customer?.name}</span>
              </p>`
                     : ``
                 }
     
            <p style='color: #64748b; font-size: 16px; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 4px'>
              <span style='font-weight: 600;'>Ticket # | رقم الفاتورة</span>
              <span>#${invoice?.number}</span>
            </p>
          </td>
        </tr>

        <!-- Items section -->
        <tr>
          <td style='padding: 16px;'>
            <table width='100%' style='border-collapse: collapse;'>
              <tr style='border-bottom: 1px solid #e2e8f0;'>
                <th style='text-align: left; padding-bottom: 8px; color: ${business.primaryColor}; font-size: 12px;'>ITEM | الصنف</th>
                <th style='text-align: center; padding-bottom: 8px; color: ${business.primaryColor}; font-size: 12px;'>QTY | الكمية</th>
                <th style='text-align: right; padding-bottom: 8px; color: ${business.primaryColor}; font-size: 12px;'>${currency}</th>
              </tr>
              ${map(invoice?.items, (d) => {
                const displayPrice = d.unitPrice !== undefined ? d.unitPrice : d.price;
                const unitInfo = d.unitName ? ` (${d.unitName})` : '';
                return ` <tr style='border-bottom: 1px solid #f1f5f9;'>
                  <td style='padding: 8px 0; color: #0f172a;'>
                    <div style='font-weight: 500;'>${d.name}${unitInfo}</div>
                    ${d.nameAr ? `<div style='color: #64748b; margin-top: 2px; margin-bottom: 2px;'>${d.nameAr}</div>` : ''}
                    ${d.unitPrice !== undefined ? `<div style='color: #64748b; font-size: 11px;'>${displayPrice} ${currency} per ${d.unitName}</div>` : ''}
                  </td>
                  <td style='padding: 8px 0; color: #334155; text-align: center;'>${d.quantity}</td>
                  <td style='padding: 8px 0; color: #334155; text-align: right;'>${(calculateItemPrice(d) * d.quantity).toFixed(2)}</td>
                </tr>`;
              })}
      
            </table>
          </td>
        </tr>

        <!-- Modifiers section -->
        ${
          size(invoice?.modifierOptions) > 0
            ? `<tr>
            <td style='padding: 0 16px 16px;'>
              <table width='100%' style='border-collapse: collapse;'>
                <tr>
                  <td colspan='3' style='color: ${business.primaryColor}; font-weight: 700; font-size: 14px; padding-top: 8px;'>Modifiers | الإضافات</td>
                </tr>
                  ${map(invoice?.modifierOptions, (d) => {
                    return `<tr>
                    <td style='color: #64748b; font-size: 12px;'>
                      + ${d.name}
                       ${d.nameAr ? `<div style='color: #64748b; font-size: 12px;'>${d.nameAr}</div>` : ``} 
                    </td>
                    <td style='color: #334155; font-size: 12px; text-align: center;'>${d.quantity}</td>
                    <td style='color: #334155; font-size: 12px; text-align: right;'>${(d.price * d.quantity).toFixed(2)}</td>
                  </tr>`;
                  })}
              </table>
            </td>
          </tr>`
            : ``
        }
          

        <!-- Totals section -->
        <tr>
          <td style='padding: 0 16px 16px;'>
            <table width='100%' style='border-top: 2px solid #e2e8f0; padding-top: 12px;'>
                <tr>
                    <td style='color: #64748b; font-weight: 500;'>Subtotal | المجموع الفرعي</td>
                    <td style='color: #334155; text-align: right;'>${getTicketSubTotal(invoice)} ${currency}</td>
                </tr>
                <tr>
                    <td style='color: #64748b; font-weight: 500;'>Discounts |  الخصومات</td>
                    <td style='color: #334155; text-align: right;'>${getTicketDiscount(invoice)} ${currency}</td>
                </tr>           
                <tr style='font-size: 15px;'>
                    <td style='color: #0f172a; font-weight: 700; padding-top: 5px;'>Total | المجموع</td>
                    <td style='color: #0f172a; font-weight: 700; text-align: right; padding-top: 5px;'>${invoice?.total} ${currency}</td>
                </tr>
                <tr style='font-size: 15px;'>
                    <td colspan="2" style='color: #0f172a; font-weight: 700; text-align: center; padding-top: 5px;'>${invoice?.payType}</td>
                </tr>
            </table>
          </td>
        </tr>

        <!-- Footer section with refund policy and store information -->
        <tr>
          <td style='padding: 16px; border-top: 1px solid #e2e8f0; background-color: #f8fafc;'>
            ${
              business.footerText
                ? `<p style='color: #64748b; font-size: 12px; margin-top: 12px;'>
                    ${business.footerText}
                </p>`
                : ``
            }
            

            <!-- Store Information centralized -->
            <div style='text-align: center; margin-top: 16px;'>
                <p style='color: #0f172a; font-weight: 600; font-size: 14px;'>Store Information | معلومات المتجر</p>
                ${business.name ? ` <p style='color: #64748b; font-size: 13px;'>${business.name}</p>` : ``}
                ${business.address ? ` <p style='color: #64748b; font-size: 13px;'>${business.address}</p>` : ``}
                ${business.phone ? `<p style='color: #64748b; font-size: 13px;'>Telephone: ${business.phone}</p>` : ``}
            </div>

            <!-- QR Code Placeholder -->
            ${
              qrCodeBase64
                ? `<div style='text-align: center; margin-top: 16px;'>
              <img src='${qrCodeBase64}' alt='QR Code' style='width: 100px; height: 100px;' />
            </div>`
                : ``
            }
            
            <div style='text-align: center; margin-top: 16px; margin-bottom: 16px;'>
              <p style='color: #64748b; font-size: 13px;'>By Orino</p>
            </div>
          </td>
        </tr>
      </table>
    </div>`;
};

export {
  calculateItemPrice,
  exportInvoiceHtml,
  getTicketDiscount,
  getTicketSubTotal,
  getTicketTotal,
  validateTicket,
};
